import type {ReactElement} from 'react';
import React, {useEffect, useRef, useCallback} from 'react';
import type {InfiniteScrollVerticalListProps} from './infinite-scroll-vertical-list.types';

const DEFAULT_FETCH_LIMIT = 20;

export function InfiniteScrollVerticalList<T>({
  fetchItems,
  renderItem,
  renderSkeleton,
  fetchLimit = DEFAULT_FETCH_LIMIT,
  className,
  items,
  hasMore,
  isLoading,
  containScroll = false,
  ...props
}: InfiniteScrollVerticalListProps<T>): ReactElement {
  const loaderRef = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const handleFetchMore = useCallback(() => {
    if (hasMore && !isLoading) {
      void fetchItems();
    }
  }, [fetchItems, hasMore, isLoading]);

  useEffect(() => {
    const handleObserver = (entries: IntersectionObserverEntry[]): void => {
      const target = entries[0];
      if (target.isIntersecting) {
        handleFetchMore();
      }
    };

    observer.current = new IntersectionObserver(handleObserver, {
      root: props.containerRef?.current ?? null,
    });
    const currentLoader = loaderRef.current;

    if (currentLoader) {
      observer.current.observe(currentLoader);
    }

    return (): void => {
      if (currentLoader) observer.current?.unobserve(currentLoader);
    };
  }, [handleFetchMore]);

  useEffect(() => {
    const container = props.containerRef?.current;
    if (!containScroll || !container) {
      return;
    }

    const handleWheel = (e: WheelEvent): void => {
      e.preventDefault();
      container.scrollTop += e.deltaY;
      container.scrollLeft += e.deltaX;
    };

    container.addEventListener('wheel', handleWheel, {passive: false});
    return (): void => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [containScroll, props.containerRef]);

  const getSkeletons = (): ReactElement[] | null => {
    if (!isLoading) {
      return null;
    }

    return Array.from({length: fetchLimit}).map((_, i) => <React.Fragment key={`skeleton-${i}`}>{renderSkeleton(i)}</React.Fragment>);
  };

  return (
    <div ref={props.containerRef} className={className}>
      {getSkeletons()}
      {items.map(renderItem)}
      {hasMore ? <div ref={loaderRef} /> : null}
    </div>
  );
}
